/**
 * PartnerLogosStrip.jsx
 * ---------------------
 * Öffentliche Partner-Sektion, wird global im PublicLayout direkt über dem Footer angezeigt.
 * Fetcht /api/public/partners und rendert bis zu 8 Logos in einem responsiven Grid mit
 * einheitlicher Höhe. Klick → Partner-Website (target=_blank rel=nofollow noopener).
 * Kein Rendering, wenn active=false oder partners=[].
 */
import React, { useEffect, useState } from 'react';
import { ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const PartnerLogosStrip = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/public/partners`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (alive) {
          setData(d);
          setLoading(false);
        }
      })
      .catch(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  if (loading) return null;
  if (!data || !data.active) return null;
  const partners = Array.isArray(data.partners) ? data.partners : [];
  if (partners.length === 0) return null;

  return (
    <section
      data-testid="partner-logos-strip"
      className="bg-white border-t border-gray-100 py-10 lg:py-14"
      aria-label="Unsere Partner und Empfehlungen"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-6 lg:mb-8">
          <p className="text-brand-gold text-xs font-semibold tracking-[0.24em] uppercase mb-2">
            Vertrauensvoll · Zusammen
          </p>
          <h2 className="font-heading text-2xl lg:text-3xl text-brand-navy">
            Unsere Partner &amp; Empfehlungen
          </h2>
          <p className="text-gray-500 text-sm mt-2 max-w-2xl mx-auto">
            Handwerker, Banken und Dienstleister, mit denen wir gerne und dauerhaft zusammenarbeiten.
          </p>
        </div>

        <div
          className={
            'grid gap-4 sm:gap-5 items-stretch justify-items-center ' +
            (partners.length <= 4
              ? 'grid-cols-2 sm:grid-cols-4'
              : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-' + Math.min(partners.length, 8))
          }
          data-testid="partner-logos-grid"
        >
          {partners.map((p) => {
            const logoSrc = p.logo_url && p.logo_url.startsWith('http')
              ? p.logo_url
              : `${API}${p.logo_url || ''}`;
            const websiteHref = p.website_url && p.website_url.startsWith('http')
              ? p.website_url
              : `https://${p.website_url || ''}`;
            return (
              <a
                key={`${p.kind}-${p.id}`}
                href={websiteHref}
                target="_blank"
                rel="nofollow noopener noreferrer"
                data-testid={`partner-logo-${p.kind}-${p.id}`}
                title={`${p.name} — Website öffnen`}
                className="group w-full max-w-[180px] h-20 sm:h-24 flex items-center justify-center rounded-lg border border-gray-100 bg-white hover:border-brand-gold/40 hover:shadow-md transition-all duration-200 p-4 relative overflow-hidden"
              >
                <img
                  src={logoSrc}
                  alt={p.name}
                  loading="lazy"
                  className="max-h-full max-w-full object-contain filter grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-200"
                  onError={(e) => {
                    // Fallback: name display if logo fails
                    e.currentTarget.style.display = 'none';
                    const fb = e.currentTarget.nextElementSibling;
                    if (fb) fb.style.display = 'flex';
                  }}
                />
                <span
                  className="absolute inset-0 hidden items-center justify-center text-xs font-semibold text-brand-navy px-2 text-center"
                >
                  {p.name}
                </span>
                <span className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <ExternalLink className="w-3 h-3 text-brand-gold" />
                </span>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PartnerLogosStrip;
